import { ProductCategory } from '../model/product-category.model';
import { MeterpointStatus } from '../model/meterpoint-status.model';
import { RechnungStatus } from '../model/rechnung-status.model';
import { Address } from '@mwe/models';
import { FilterOption } from '@shared-ikp/model/filter-options.model';
import { LegacyUmmeldungStatus } from '../model/meter-point.model';
import { Company } from '@shared-ikp/model/company.model';
import { concatCompanyNameAndGpNr } from '../service/company/company.utils';
import { getAddressStreetAndPostCodeFormatted } from '@mwe/utils';
import {
  getInvoiceStatusTranslation,
  getMeterpointStatusTranslation,
  getProductCategoryTranslation,
} from './ikp-dropdown-transformers.utils';

type MeterpointStatusTransformInput =
  | (typeof MeterpointStatus | typeof LegacyUmmeldungStatus)
  | (typeof MeterpointStatus | typeof LegacyUmmeldungStatus)[];
type MeterpointStatusTransformOutput = FilterOption<MeterpointStatus | LegacyUmmeldungStatus>[];

export type IKPTransformerKeys = keyof typeof dropdownTypeMapping;
export type IKPSupportedDropdownTypes = Parameters<(typeof dropdownTypeMapping)[IKPTransformerKeys]>[0];
export type IKPReturnTypeMap = { [K in IKPTransformerKeys]: ReturnType<(typeof dropdownTypeMapping)[K]>[number] };

function ProductCategoryEnumTransformer(value: typeof ProductCategory): FilterOption<ProductCategory>[] {
  return Object.values(value).map(category => ({ text: getProductCategoryTranslation(category), value: category }));
}

function MeterpointStatusEnumTransformer(value: MeterpointStatusTransformInput): MeterpointStatusTransformOutput {
  const enums = Array.isArray(value) ? value : [value];
  const allValues = enums.flatMap(enumObj => Object.values(enumObj));
  return allValues.map(status => ({
    text: getMeterpointStatusTranslation(status),
    value: status as MeterpointStatus | LegacyUmmeldungStatus,
  }));
}

type RechnungStatusTransformInput = {
  rechnungStatus: typeof RechnungStatus;
  isRechnungV2ViewEnabled: boolean;
};

function RechnungsStatusEnumTransformer(input: RechnungStatusTransformInput): FilterOption<RechnungStatus>[] {
  const { rechnungStatus, isRechnungV2ViewEnabled } = input;
  const ignoredStatuses: RechnungStatus[] = [
    RechnungStatus.MAHNUNG_1,
    RechnungStatus.MAHNUNG_2,
    RechnungStatus.REKLAMIERT,
    RechnungStatus.STORNIERT,
  ];

  if (!isRechnungV2ViewEnabled) {
    ignoredStatuses.push(RechnungStatus.TEILBEZAHLT);
  }

  return Object.values(rechnungStatus)
    .filter(status => !ignoredStatuses.includes(status))
    .map(status => ({
      text: getInvoiceStatusTranslation(status),
      value: status,
    }));
}

function AddressArrayTransformer(companies: Address[]): FilterOption<Address>[] {
  return companies.map(address => {
    const addressWithoutDoorNr = { ...address, doorNumber: null };
    const text = getAddressStreetAndPostCodeFormatted(addressWithoutDoorNr);
    return {
      text,
      value: {
        ...addressWithoutDoorNr,
        addressGroup: { ...addressWithoutDoorNr.addressGroup, groupName: text },
      },
    };
  });
}

function CompanyArrayTransformer(value: Company[]): FilterOption[] {
  return value.map(c => ({ text: concatCompanyNameAndGpNr(c), value: c.gpNr }));
}

export const dropdownTypeMapping = {
  ProductCategory: ProductCategoryEnumTransformer,
  MeterpointStatus: MeterpointStatusEnumTransformer,
  RechnungStatus: RechnungsStatusEnumTransformer,
  AddressArray: AddressArrayTransformer,
  CompanyArray: CompanyArrayTransformer,
} as const;
